import { useRef } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography } from '../ui';
import { uploadFile, alert } from '../../hooks/function';

interface CourseDetailPageSectionProps {
  // Controlled — round-trips through the parent's course form payload
  // in a single save call.
  detailImageUrl: string;
  onDetailImageUrlChange: (url: string) => void;
}

/**
 * "Detail Page" admin section. The legacy Wistia/Mux "Detail Video"
 * has been retired in favor of the simpler Sample Video URL field
 * downstream. The Custom tabs editor lives at the bottom of the page
 * rather than inside this section.
 */
export default function CourseDetailPageSection({
  detailImageUrl,
  onDetailImageUrlChange,
}: CourseDetailPageSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageFileSelected = async (file: File) => {
    try {
      const result = await uploadFile(file);
      if (result?.uploadedUrl) {
        onDetailImageUrlChange(result.uploadedUrl);
      } else {
        alert(enqueueSnackbar, 'Image upload failed', 'error');
      }
    } catch (e) {
      console.error('Image upload error:', e);
      alert(enqueueSnackbar, 'Image upload failed', 'error');
    }
  };

  return (
    <Box>
      {/* Heading + subtitle now live on the parent Card; this
          component just exposes the image picker. */}
      <Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {detailImageUrl ? (
            <Box
              component="img"
              src={detailImageUrl}
              alt="Detail image preview"
              sx={{ width: 240, height: 135, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            />
          ) : (
            <Box
              sx={{
                width: 240,
                height: 135,
                bgcolor: 'grey.100',
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No image
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleImageFileSelected(f);
              }}
            />
            <Button size="small" variant="contained" color="primary" onClick={() => fileInputRef.current?.click()}>
              {detailImageUrl ? 'Replace Image' : 'Upload Image'}
            </Button>
            {detailImageUrl && (
              <Button size="small" variant="text" color="inherit" onClick={() => onDetailImageUrlChange('')}>
                Remove image
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Custom tabs editor moved to the bottom of the page — see
          CourseDetailPageTab. */}
    </Box>
  );
}
